import * as Sentry from "@sentry/react";
import type { AppRole } from "@/types/database";

export type SentryEnvironment = "local" | "development" | "staging" | "production";

const VALID_SENTRY_ENVIRONMENTS = new Set<SentryEnvironment>([
  "local",
  "development",
  "staging",
  "production",
]);

export type FrontendBreadcrumbOptions = {
  category?: string;
  data?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
};

export interface InnovaCoreSentryBridge {
  error: (label?: string) => string;
  message: (label?: string) => string;
  trace: (label?: string) => string;
  context: () => SentryScopeSnapshot;
}

const REACT_ROUTER_V7_START_TRANSITION_WARNING =
  "React Router Future Flag Warning";
const REACT_ROUTER_V7_START_TRANSITION_FLAG = "v7_startTransition";

type SentryScopeSnapshot = {
  enabled: boolean;
  environment: SentryEnvironment | null;
  initialized: boolean;
  transactionName: string | null;
  user: Sentry.User | null;
  tags: Record<string, unknown>;
  contexts: Record<string, unknown>;
  breadcrumbs: Array<{
    category?: string;
    data?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
    message?: string;
    timestamp?: number;
  }>;
};

type SerializableBreadcrumb = SentryScopeSnapshot["breadcrumbs"][number];
type ScopeDataForSnapshot = Pick<
  ReturnType<Sentry.Scope["getScopeData"]>,
  "breadcrumbs" | "contexts" | "tags" | "transactionName" | "user"
>;

const RESERVED_TOP_LEVEL_ROUTE_SEGMENTS = new Set([
  "admin",
  "docs",
  "changelog",
  "contacto",
  "env",
  "environments",
  "login",
  "me",
  "plugin-auth",
  "team",
]);

const OWNER_ROUTE_SECTIONS = new Set([
  "audit",
  "environments",
  "metrics",
  "mcp-catalog",
  "model-catalog",
  "observability",
  "overview",
  "people",
  "resources",
  "settings",
  "teams",
  "users",
]);

declare global {
  interface Window {
    __INNOVACORE_SENTRY__?: InnovaCoreSentryBridge;
    __INNOVACORE_SENTRY_HOSTED_SMOKE__?: () => string;
  }
}

export function isSentryEnvironment(value?: string): value is SentryEnvironment {
  return !!value && VALID_SENTRY_ENVIRONMENTS.has(value as SentryEnvironment);
}

function isLocalHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "";
}

export function resolveSentryEnvironmentFromHost(
  rawEnvironment?: string,
  hostname?: string,
): SentryEnvironment | null {
  const normalizedEnvironment = rawEnvironment?.trim().toLowerCase();
  if (isSentryEnvironment(normalizedEnvironment)) {
    return normalizedEnvironment;
  }

  const normalizedHost = hostname?.trim().toLowerCase() ?? "";
  if (isLocalHost(normalizedHost)) {
    return "local";
  }
  if (normalizedHost.endsWith(".test.innovacore.dev")) {
    return "development";
  }
  if (normalizedHost.endsWith(".stg.innovacore.dev")) {
    return "staging";
  }
  if (
    normalizedHost === "innovacore.dev" ||
    normalizedHost.endsWith(".innovacore.dev")
  ) {
    return "production";
  }

  return null;
}

export function shouldExposeInnovaCoreSentryBridge(
  isDev: boolean,
  environment: SentryEnvironment | null,
): boolean {
  return isDev && environment !== "production";
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function resolveRouteOrgIdFromPathname(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  if (segments[0] === "admin" && segments[1] === "org" && segments[2]) {
    return decodePathSegment(segments[2]);
  }

  if (
    segments.length >= 2 &&
    OWNER_ROUTE_SECTIONS.has(segments[1]) &&
    !RESERVED_TOP_LEVEL_ROUTE_SEGMENTS.has(segments[0])
  ) {
    return decodePathSegment(segments[0]);
  }

  return null;
}

type ResolveSentryCurrentOrgIdInput = {
  currentOrgId?: string | null;
  pathname?: string | null;
  tenantOrgId?: string | null;
};

export function resolveSentryCurrentOrgId({
  currentOrgId,
  pathname,
  tenantOrgId,
}: ResolveSentryCurrentOrgIdInput): string | null {
  const normalizedPathname = pathname ?? "";
  const routeOrgId = resolveRouteOrgIdFromPathname(normalizedPathname);
  if (routeOrgId) {
    return routeOrgId;
  }

  const [topLevelSegment] = normalizedPathname.split("/").filter(Boolean);
  if (topLevelSegment === "admin") {
    return null;
  }

  return currentOrgId ?? tenantOrgId ?? null;
}

export function resolveSentryTransactionNameFromRoutePaths(
  routePaths: Array<string | undefined>,
  fallbackPathname?: string | null,
): string {
  let resolvedPath: string | null = null;

  for (const routePath of routePaths) {
    if (!routePath) continue;

    if (routePath === "/") {
      resolvedPath ??= "/";
      continue;
    }

    if (routePath.startsWith("/")) {
      resolvedPath = routePath;
      continue;
    }

    const normalizedSegment = routePath.replace(/^\/+/, "");
    if (!resolvedPath || resolvedPath === "/") {
      resolvedPath = `/${normalizedSegment}`;
      continue;
    }

    resolvedPath = `${resolvedPath.replace(/\/+$/, "")}/${normalizedSegment}`;
  }

  return resolvedPath ?? fallbackPathname ?? "/";
}

export function addFrontendBreadcrumb(
  message: string,
  options: FrontendBreadcrumbOptions = {},
): void {
  Sentry.addBreadcrumb({
    category: options.category ?? "app",
    data: options.data,
    level: options.level ?? "info",
    message,
  });
}

export function shouldDropFrontendBreadcrumb(
  breadcrumb: Pick<Sentry.Breadcrumb, "category" | "message">,
): boolean {
  const message = breadcrumb.message ?? "";

  return (
    breadcrumb.category === "console" &&
    message.includes(REACT_ROUTER_V7_START_TRANSITION_WARNING) &&
    message.includes(REACT_ROUTER_V7_START_TRANSITION_FLAG)
  );
}

export function captureFrontendOperationalError(
  name: string,
  error: unknown,
  metadata?: Record<string, unknown>,
): string {
  const exception =
    error instanceof Error
      ? error
      : new Error(typeof error === "string" ? error : `Unexpected error in ${name}`);

  return Sentry.withScope((scope) => {
    scope.setTag("app_surface", "hub-web");
    scope.setContext("frontend_operational_error", {
      name,
      ...metadata,
    });
    return Sentry.captureException(exception);
  });
}

type SyncSentryAuthScopeInput = {
  currentOrgId?: string | null;
  isImpersonating: boolean;
  orgId?: string | null;
  userId?: string | null;
  userRole?: AppRole | null;
};

export function syncSentryAuthScope({
  currentOrgId,
  isImpersonating,
  orgId,
  userId,
  userRole,
}: SyncSentryAuthScopeInput): void {
  Sentry.setTag("app_surface", "hub-web");
  Sentry.setTag("user_role", userRole ?? "anonymous");
  Sentry.setTag("is_impersonating", String(isImpersonating));
  Sentry.setContext("tenant", orgId ? { org_id: orgId } : null);
  Sentry.setContext(
    "auth",
    userId
      ? {
          current_org_id: currentOrgId ?? orgId ?? null,
          is_impersonating: isImpersonating,
        }
      : null,
  );
  Sentry.setUser(userId ? { id: userId } : null);
}

export function syncSentryTransactionName(transactionName: string): void {
  const normalizedTransactionName = transactionName || "/";
  Sentry.getIsolationScope().setTransactionName(normalizedTransactionName);
  Sentry.getCurrentScope().setTransactionName(normalizedTransactionName);
}

export function clearSentryAuthScope(): void {
  syncSentryAuthScope({
    currentOrgId: null,
    isImpersonating: false,
    orgId: null,
    userId: null,
    userRole: null,
  });
}

function normalizeBridgeLabel(prefix: string, label?: string): string {
  const normalizedLabel = label
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalizedLabel ? `${prefix}:${normalizedLabel}` : prefix;
}

function serializeBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
): SerializableBreadcrumb {
  return {
    category: breadcrumb.category,
    data: breadcrumb.data,
    level: breadcrumb.level,
    message: breadcrumb.message,
    timestamp: breadcrumb.timestamp,
  };
}

export function mergeScopeDatasForSnapshot(
  scopeDatas: ScopeDataForSnapshot[],
): Pick<
  SentryScopeSnapshot,
  "user" | "tags" | "contexts" | "breadcrumbs" | "transactionName"
> {
  const merged: Pick<
    SentryScopeSnapshot,
    "user" | "tags" | "contexts" | "breadcrumbs" | "transactionName"
  > = {
    transactionName: null,
    user: null,
    tags: {},
    contexts: {},
    breadcrumbs: [],
  };

  for (const scopeData of scopeDatas) {
    if (scopeData.user && Object.keys(scopeData.user).length > 0) {
      merged.user = scopeData.user;
    }
    if (scopeData.transactionName) {
      merged.transactionName = scopeData.transactionName;
    }

    Object.assign(merged.tags, scopeData.tags ?? {});
    Object.assign(merged.contexts, scopeData.contexts ?? {});
    merged.breadcrumbs.push(
      ...(scopeData.breadcrumbs ?? []).map((breadcrumb) =>
        serializeBreadcrumb(breadcrumb),
      ),
    );
  }

  return {
    ...merged,
    breadcrumbs: merged.breadcrumbs.slice(-10),
  };
}

export function getSentryScopeSnapshot(
  environment: SentryEnvironment | null,
): SentryScopeSnapshot {
  const scopeData = mergeScopeDatasForSnapshot([
    Sentry.getGlobalScope().getScopeData(),
    Sentry.getIsolationScope().getScopeData(),
    Sentry.getCurrentScope().getScopeData(),
  ]);

  return {
    enabled: environment !== null && environment !== "local",
    environment,
    initialized: Sentry.isInitialized(),
    transactionName: scopeData.transactionName ?? null,
    user: scopeData.user ?? null,
    tags: scopeData.tags ?? {},
    contexts: scopeData.contexts ?? {},
    breadcrumbs: scopeData.breadcrumbs ?? [],
  };
}

export function installInnovaCoreSentryBridge(
  environment: SentryEnvironment | null,
): void {
  if (typeof window === "undefined") return;

  if (!shouldExposeInnovaCoreSentryBridge(import.meta.env.DEV, environment)) {
    delete window.__INNOVACORE_SENTRY__;
    return;
  }

  window.__INNOVACORE_SENTRY__ = {
    error(label?: string) {
      const message = normalizeBridgeLabel("innovacore.sentry.test.error", label);
      addFrontendBreadcrumb("Manual Sentry test error captured", {
        category: "qa.sentry",
        data: { message },
      });
      return Sentry.captureException(new Error(message));
    },
    message(label?: string) {
      const message = normalizeBridgeLabel("innovacore.sentry.test.message", label);
      addFrontendBreadcrumb("Manual Sentry test message captured", {
        category: "qa.sentry",
        data: { message },
      });
      return Sentry.captureMessage(message);
    },
    trace(label?: string) {
      const name = normalizeBridgeLabel("innovacore.sentry.test.trace", label);
      addFrontendBreadcrumb("Manual Sentry test trace started", {
        category: "qa.sentry",
        data: { name },
      });
      Sentry.startSpan(
        {
          name,
          op: "qa.sentry.trace",
          attributes: {
            "app.surface": "hub-web",
            "qa.sentry.name": name,
          },
        },
        (span) => {
          span.setAttribute("qa.sentry.completed", true);
        },
      );
      return name;
    },
    context() {
      return getSentryScopeSnapshot(environment);
    },
  };
}
